<template>
  <BasicTable
    ref="tableRef"
    :class="[{ 'editable-table-quick-mode': quickAddDel }]"
    v-bind="getBindValue"
    @register="registerTable"
  >
    <template v-for="item in Object.keys($slots)" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>

    <template #emptyText="data">
      <slot name="emptyText" v-bind="data || {}">
        <TableEmptyAdd :disabled="disabled" :action="addAction">
          <InputNumber
            v-if="!disabled && quickAddDel && addAction"
            v-model:value="numValue"
            v-bind="rowNumberProps"
          />
        </TableEmptyAdd>
      </slot>
    </template>

    <template v-if="!disabled && quickAddDel && addAction" #footer="data">
      <slot name="footer" v-bind="data || {}">
        <div class="table-footer-qiuck-bar flex justify-between">
          <div class="gap-6px inline-flex items-center justify-center">
            <MButton v-bind="addAction">
              <template v-if="addAction.label">{{ t(addAction.label) }}</template>
            </MButton>
            <InputNumber v-model:value="numValue" v-bind="rowNumberProps" />
          </div>
          <MButton type="link" color="error" :icon="h(DeleteOutlined)" @click="clearEmptyRow">
            {{ t('g.25090014') }}
          </MButton>
        </div>
      </slot>
    </template>
  </BasicTable>
</template>

<script setup lang="ts">
  import { DeleteOutlined } from '@ant-design/icons-vue';
  import { InputNumber } from 'ant-design-vue';
  import { merge } from 'lodash-es';
  import { computed, h, ref, unref, useAttrs } from 'vue';
  import type { ActionBtnItem, ActionHandlerFn, TableCellChanged } from '@mts/render';
  import { isIfShow, omit, pick } from '@mts-fe/utils';
  import {
    ActionItem,
    BasicColumn,
    BasicTable,
    BasicTableProps,
    TableEmptyAdd,
    useTable,
  } from '@mts/components/Table';
  import { t } from '@mts/hooks/web/useI18n';
  import { usePermission } from '@mts/hooks/web/usePermission';
  import { getGlobalParams } from '@mts/render';
  import {
    TABLE_EMIT_EVENTS,
    useColumnFetch,
    useDynamicColumns,
    useTableEvents,
  } from '@mts/render/hooks/useRenderTable';

  /** @deprecated 已废弃 */
  defineOptions({
    name: 'EditableTable',
  });

  const props = withDefaults(
    defineProps<{
      editable?: boolean;
      disabled?: boolean;
      quickAddDel?: boolean;
      actionHandler?: ActionHandlerFn;
    }>(),
    { editable: true },
  );

  const emit = defineEmits(['table-data-change', ...TABLE_EMIT_EVENTS]);
  const bindTableEvents = useTableEvents(emit, (evt: string, ...args: Recordable[]) => {
    if (evt === 'edit-change') {
      const { value, column, record, index } = args[0];
      handleChange(value, column?.dataIndex, record, index, ...args);
    }
  });
  const handleChange: TableCellChanged = (...rest) => {
    emit('table-data-change', tableAction.getDataSource(), ...rest);
  };

  const attrs = useAttrs();
  const getBindValue = computed(() => ({
    ...bindTableEvents,
    ...attrs,
    ...omit(props, ['disabled', 'quickAddDel', 'addRowNum']),
  }));
  const { hasPermission } = usePermission();
  const [registerTable, tableAction] = useTable();
  const dynamicAction = useDynamicColumns(tableAction);

  const addAction = ref<ActionItem>();
  const tableColumns = ref<BasicColumn[]>([]);
  const setProps: (tableProps: Partial<BasicTableProps>) => void = ({
    actions = [],
    ..._tableProps
  }) => {
    const globalParams = getGlobalParams();
    addAction.value = actions
      .filter(
        (i) =>
          i.eventType === 'RECORD_ADD' &&
          hasPermission(i.auth) &&
          isIfShow<ActionItem>(i, globalParams),
      )
      .map((item) => {
        const handler = item.onClick || (props.quickAddDel ? addRowByNum : props.actionHandler);
        return {
          ...item,
          onClick: handler?.bind(null, item, void 0, tableAction.getDataSource().length),
        } as ActionItem;
      })[0];

    const defProps: BasicTableProps = {
      columns: [],
      canResize: false,
      pagination: false,
      actionHandler: props.actionHandler,
      actions,
    };
    const mergedProps = merge(defProps, _tableProps, {
      actionColumn:
        !props.disabled && _tableProps.actionColumn
          ? { ..._tableProps.actionColumn, dataIndex: 'ACTION' }
          : null,
    });
    useColumnFetch(mergedProps.columns, tableAction);
    tableColumns.value = mergedProps.columns;
    tableAction.setProps(mergedProps);
  };

  const numValue = defineModel<number>('addRowNum', { default: 10 });
  const rowNumberProps = {
    class: '!w-64px',
    min: 1,
    max: 99,
    precision: 0,
    addonAfter: t('g.25090015'),
  };
  const addRowByNum = (_: ActionBtnItem) => {
    tableAction.insertTableDataRecord(
      new Array(numValue.value).fill(Date.now().toString()).map((i, index) => ({ key: i + index })),
      tableAction.getDataSource().length,
    );
    handleChange({}, '', {});
  };
  const clearEmptyRow = () => {
    // @ts-ignore
    const columnKeys = tableColumns.value.map((i) => i.dataIndex?.toString() || '').filter(Boolean);
    const tableData = tableAction.getDataSource().filter((row) => {
      const values = Object.values(pick(row, columnKeys));
      return values.length && values.filter(Boolean).length;
    });
    tableAction.setTableData(tableData);
    handleChange({}, '', {});
  };

  const tableRef = ref();
  defineExpose({
    ...tableAction,
    ...dynamicAction,
    setProps,
    getTableElRef: () => unref(tableRef.value?.tableElRef)?.$el,
  });
</script>

<style lang="less">
  .editable-table-quick-mode .ant-table-empty .ant-table-footer {
    display: none;
  }
</style>
